import * as React from 'react';
import { useEffect, useMemo } from 'react';

import find from 'lodash/find';
import groupBy from 'lodash/groupBy';
import values from 'lodash/values';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import Retracked from 'js/lib/retracked';

import { Link, TabPanel } from '@coursera/cds-core';

import S12nToCourseraPlusUpgradeContainer from 'bundles/coursera-plus/components/s12nToCourseraPlusUpgrade/S12nToCourseraPlusUpgradeContainer';
import LoadingIcon from 'bundles/courseraLoadingIcon/LoadingIcon';
import ManageSubscriptionCard from 'bundles/my-purchases-graphql/components/manage-subscriptions/ManageSubscriptionCard';
import { useGetLoggedInUserSubscriptions } from 'bundles/my-purchases-graphql/components/utils/useGetLoggedInUserSubscriptions';
import { BROWSE_CATALOG_LINK } from 'bundles/my-purchases-graphql/lib/constants';
import type SubscriptionsV1 from 'bundles/naptimejs/resources/subscriptions.v1';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import {
  generateImpressionForResubscribeAfterCancellationPeriod,
  useResubscriptionAfterCancellationPeriod,
} from 'bundles/subscriptions/utils/resubscriptionUtils';

import _t from 'i18n!nls/my-purchases-graphql';

export type Props = {
  subscriptions: Array<SubscriptionsV1>;
};

type IProps<P> = React.ComponentType<P> & {
  LOADING_COMPONENT: typeof React.Component;
};

export const ManageSubscriptionsTab: IProps<Props> = ({ subscriptions }) => {
  const { resubscriptionEligibleSubscriptions } = useResubscriptionAfterCancellationPeriod(subscriptions);

  const subscriptionData = useMemo(() => {
    const existingCourseraLiteSubscription = subscriptions.find(
      (subscription) => subscription.isCourseraLiteSubscription
    );

    const subscriptionGroups = groupBy(subscriptions, (subscription) => subscription.productSku);
    return values(subscriptionGroups).map((subscriptionGroup) => {
      const subscription = find(subscriptionGroup, { isActive: true }) || subscriptionGroup[0];
      const { id, isActive, isFreeTrial, isCourseraPlusSubscription } = subscription;
      const isLiteToPlusUpgrade = isCourseraPlusSubscription && Boolean(existingCourseraLiteSubscription);
      const isResubscriptionEligible = resubscriptionEligibleSubscriptions.some(
        (s) => s.productItemId === subscription.productItemId
      );
      return {
        subscriptionId: id,
        isActive,
        isFreeTrial,
        isLiteToPlusUpgrade,
        isResubscriptionEligible,
      };
    });
  }, [subscriptions, resubscriptionEligibleSubscriptions]);

  // Impress the user in the resubscribeAfterCancellationPeriod experiment if they have eligible subscriptions
  useEffect(() => {
    generateImpressionForResubscribeAfterCancellationPeriod(resubscriptionEligibleSubscriptions);
  }, [resubscriptionEligibleSubscriptions]);

  const tabBody = useMemo(() => {
    return (
      <ul className="tab-body nostyle">
        {subscriptionData.map((subscription) => (
          <li key={`subscription~${subscription.subscriptionId}`}>
            <ManageSubscriptionCard {...subscription} />
          </li>
        ))}
      </ul>
    );
  }, [subscriptionData]);

  if (subscriptions.length === 0) {
    return (
      <TabPanel value="subscriptions">
        <div className="rc-ManageSubscriptionsTab">
          <div data-test="empty-state">
            <FormattedMessage
              message={_t('You do not have any subscriptions. {browseCatalogLink}')}
              tagName="p"
              browseCatalogLink={
                <Link
                  component={TrackedA}
                  target="_blank"
                  rel="noopener noreferrer"
                  href={BROWSE_CATALOG_LINK}
                  trackingName="browse_catalog"
                >
                  {_t('Browse our catalog.')}
                </Link>
              }
            />
          </div>
        </div>
      </TabPanel>
    );
  }

  return (
    <TabPanel value="subscriptions">
      <S12nToCourseraPlusUpgradeContainer />
      <div className="rc-ManageSubscriptionsTab">{tabBody}</div>
    </TabPanel>
  );
};

ManageSubscriptionsTab.LOADING_COMPONENT = LoadingIcon;

const TrackedManageSubscriptionsTab = Retracked.createTrackedContainer<Props>(() => {
  return {
    namespace: {
      page: 'manage_subscriptions',
    },
  };
})(ManageSubscriptionsTab);

const TrackedManageSubscriptionsTabContainer = () => {
  const { subscriptions, pending } = useGetLoggedInUserSubscriptions({});
  if (pending || (subscriptions ?? []).length <= 0) {
    return null;
  }

  return <TrackedManageSubscriptionsTab subscriptions={subscriptions} />;
};

export default TrackedManageSubscriptionsTabContainer;
